
import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, FileCheck, X } from "lucide-react";
import { toast } from "sonner";

interface FileDropzoneProps {
  file: File | null;
  setFile: (file: File | null) => void;
  acceptedFileTypes: string;
  onClearFile: () => void;
  maxSize?: number;
}

const FileDropzone = ({ 
  file, 
  setFile, 
  acceptedFileTypes, 
  onClearFile,
  maxSize = 5 * 1024 * 1024 // Default 5MB
}: FileDropzoneProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      // Check file size
      if (maxSize && selectedFile.size > maxSize) {
        toast.error(`File too large. Maximum size is ${(maxSize / (1024 * 1024)).toFixed(1)}MB`);
        return;
      }
      
      setFile(selectedFile);
      console.log("File selected:", selectedFile.name);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      
      // Check file size
      if (maxSize && droppedFile.size > maxSize) {
        toast.error(`File too large. Maximum size is ${(maxSize / (1024 * 1024)).toFixed(1)}MB`);
        return;
      }
      
      const fileExtension = droppedFile.name.split('.').pop()?.toLowerCase();
      
      // Check if the file type is accepted
      if (acceptedFileTypes.includes(`.${fileExtension}`)) {
        setFile(droppedFile);
        console.log("File dropped:", droppedFile.name);
      } else {
        toast.error(`Invalid file type. Please upload ${acceptedFileTypes.replace(/\./g, "").toUpperCase()}`);
      }
    }
  };

  const handleBrowseClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div 
      className="flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 mb-4 bg-muted/50"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {file ? (
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <FileCheck className="h-8 w-8 text-fraudeye-accent" />
            <div>
              <p className="font-medium">{file.name}</p>
              <p className="text-xs text-muted-foreground">
                {(file.size / 1024).toFixed(2)} KB
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClearFile}
            type="button"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <>
          <Upload className="h-12 w-12 text-muted-foreground mb-4" />
          <div className="text-center mb-4">
            <p className="font-medium mb-1">
              Drag and drop or click to upload
            </p>
            <p className="text-sm text-muted-foreground">
              Supports {acceptedFileTypes.replace(/\./g, "").toUpperCase()}
              {maxSize && ` (Max: ${(maxSize / (1024 * 1024)).toFixed(1)}MB)`}
            </p>
          </div>
          <Input
            type="file"
            accept={acceptedFileTypes}
            onChange={handleFileChange}
            className="hidden"
            id="file-upload"
            ref={fileInputRef}
          />
          <Button 
            variant="outline" 
            type="button"
            onClick={handleBrowseClick}
            className="cursor-pointer"
          >
            Select File
          </Button>
        </>
      )}
    </div>
  );
};

export default FileDropzone;
